// ===================================================
// answerAnalyzer.js — 답변 깊이 분석 유틸
//
// GPT 질문의 품질을 암묵적으로 평가하기 위한 헬퍼 함수 모음입니다.
//
// 핵심 아이디어:
//   사용자가 평소보다 긴 답변을 남겼다면, 그 질문이 특히 좋았다는 신호입니다.
//   (좋은 질문 → 더 많은 생각 → 더 긴 답변)
//
// 이 파일에서 제공하는 기능:
//   - computeAverageAnswerLength : 과거 기록에서 모드별 평균 답변 길이 계산
//   - findDeepAnswerQuestions    : 평균보다 긴 답변을 받은 질문 목록 추출
//   - findResonantAxes           : 의사결정 모드에서 깊은 답변을 받은 사고 축 추출
// ===================================================


/**
 * 과거 세션에서 특정 모드의 평균 답변 길이를 계산합니다.
 *
 * @param {Object} sessions  - 전체 저장 세션 { "날짜": [세션, ...] }
 * @param {string} mode      - 모드명 (예: 'diary', 'study')
 * @returns {number}         - 평균 답변 길이 (글자 수), 기록 없으면 0
 */
export function computeAverageAnswerLength(sessions, mode) {
  const lengths = [];

  Object.values(sessions).forEach(dayArr => {
    dayArr.forEach(session => {
      if (session.mode !== mode) return;
      (session.rounds || []).forEach(round => {
        (round.answers || []).forEach(ans => {
          const trimmed = (ans || '').trim();
          if (trimmed.length > 0) lengths.push(trimmed.length);
        });
      });
    });
  });

  if (lengths.length === 0) return 0;
  return lengths.reduce((a, b) => a + b, 0) / lengths.length;
}


/**
 * 이번 세션에서 평균보다 긴 답변을 받은 질문 목록을 반환합니다.
 * 이 질문들은 유저가 특히 좋아했다는 암묵적 신호로 활용됩니다.
 *
 * @param {Array}  finalRounds  - 저장 직전의 전체 라운드 [{ questions, answers }, ...]
 * @param {number} avgLength    - 모드별 평균 답변 길이 (computeAverageAnswerLength 결과)
 * @returns {string[]}          - 평균 초과 답변을 받은 질문 배열
 */
export function findDeepAnswerQuestions(finalRounds, avgLength) {
  // 평균 기록이 없거나 0이면 판단 불가
  if (avgLength <= 0) return [];

  const result = [];
  finalRounds.forEach(({ questions, answers }) => {
    (questions || []).forEach((q, i) => {
      const ans = (answers?.[i] || '').trim();
      if (ans.length > avgLength) {
        result.push(q);
      }
    });
  });
  return result;
}


/**
 * 의사결정 모드 전용:
 * 이번 세션에서 평균보다 긴 답변을 받은 사고 축(axis)을 반환합니다.
 * "공명하는 축(resonant axis)" — 이 유저가 특히 깊이 탐색한 사고 차원입니다.
 *
 * 가정: 각 라운드는 정확히 2개의 질문과 2개의 축을 생성합니다.
 *       (DECISION_INITIAL_PROMPT / DECISION_CONTINUE_PROMPT 기준)
 *       decisionAxes는 [라운드0_축0, 라운드0_축1, 라운드1_축0, ...] 형태로 누적됩니다.
 *
 * @param {Array}  finalRounds    - 전체 라운드 [{ questions, answers }, ...]
 * @param {Array}  decisionAxes   - 누적된 사고 축 배열 (useSessionHandlers의 decisionAxes 상태)
 * @param {number} avgLength      - 모드별 평균 답변 길이
 * @returns {string[]}            - 공명하는 축 이름 배열 (중복 제거)
 */
export function findResonantAxes(finalRounds, decisionAxes, avgLength) {
  if (avgLength <= 0 || !decisionAxes || decisionAxes.length === 0) return [];

  const resonant = [];
  finalRounds.forEach(({ questions, answers }, roundIdx) => {
    // 각 라운드는 2개의 축을 사용 → roundIdx * 2 위치에서 슬라이스
    const roundAxes = decisionAxes.slice(roundIdx * 2, roundIdx * 2 + 2);
    (questions || []).forEach((q, i) => {
      const ans = (answers?.[i] || '').trim();
      const axis = roundAxes[i];
      if (axis && ans.length > avgLength) {
        resonant.push(axis);
      }
    });
  });

  // 같은 축이 여러 번 나올 수 있으므로 중복 제거
  return [...new Set(resonant)];
}
